import panzoom from '../lib/panzoom';
import { clamp } from '../lib/mumath/index';

import Base from '../core/Base';
import { MAP, Modes } from '../core/Constants';
import Grid from '../grid/Grid';
import { Point } from '../geometry/Point';
import ModesMixin from './ModesMixin';
import Measurement from '../measurement/Measurement';
import { mix } from '../lib/mix';

export class Map extends mix(Base).with(ModesMixin) {
  constructor(container, options) {
    super(options);

    this.defaults = Object.assign({}, MAP);

    // set defaults
    Object.assign(this, this.defaults);

    // overwrite options
    Object.assign(this, this._options);

    this.center = new Point(this.center);

    this.container = container || document.body;

    const canvas = document.createElement('canvas');
    this.container.appendChild(canvas);
    canvas.setAttribute('id', 'indoors-map-canvas');

    canvas.width = this.width || this.container.clientWidth;
    canvas.height = this.height || this.container.clientHeight;

    this.canvas = new fabric.Canvas(canvas, {
      preserveObjectStacking: true,
      renderOnAddRemove: true
    });
    this.context = this.canvas.getContext('2d');

    this.on('render', () => {
      if (this.autostart) this.clear();
    });

    this.originX = -this.canvas.width / 2;
    this.originY = -this.canvas.height / 2;

    this.canvas.absolutePan({
      x: this.originX,
      y: this.originY
    });

    // this.center = {
    //   x: this.canvas.width / 2.0,
    //   y: this.canvas.height / 2.0
    // };

    this.x = this.center.x;
    this.y = this.center.y;
    this.dx = 0;
    this.dy = 0;

    this.padding = this.padding || [0, 0, 0, 0];

    try {
      this.addFloorPlan();
    } catch (e) {
      console.error(e);
    }

    if (this.showGrid) {
      this.addGrid();
    }

    this.setMode(this.mode || Modes.GRAB);

    const vm = this;
    panzoom(this.container, e => {
      vm.panzoom(e);
    });

    this.registerListeners();

    setTimeout(() => {
      this.emit('ready', this);
    }, 300);

    this.measurement = new Measurement(this);
  }

  addFloorPlan() {
    if (!this.floorplan) return;
    const vm = this;
    this.floorplan.on('load', img => {
      vm.addLayer(img);
    });
  }

  addLayer(layer) {
    // this.canvas.renderOnAddRemove = false;
    if (!layer.shape) {
      console.error('shape is undefined');
      return;
    }
    this.canvas.add(layer.shape);
    this.canvas._objects.sort((o1, o2) => o1.zIndex - o2.zIndex);

    if (layer.shape.keepOnZoom) {
      const scale = 1.0 / this.zoom;
      layer.shape.set('scaleX', scale);
      layer.shape.set('scaleY', scale);
      layer.shape.setCoords();
      this.emit(`${layer.class}scaling`, layer);
    }
    if (layer.class) {
      this.emit(`${layer.class}:added`, layer);
    }

    // this.canvas.renderOnAddRemove = true;

    // this.update();
    this.calculateMaxZoom();
    this.canvas.requestRenderAll();
  }

  removeLayer(layer) {
    if (!layer || !layer.shape) return;
    if (layer.class) {
      this.emit(`${layer.class}:removed`, layer);
    }
    this.canvas.remove(layer.shape);
  }

  addGrid() {
    this.gridCanvas = this.cloneCanvas();
    this.gridCanvas.setAttribute('id', 'indoors-grid-canvas');
    const options = {
      center: this.center
    };
    this.grid = new Grid(this.gridCanvas, options);
    this.grid.draw();
  }

  moveTo(obj, index) {
    if (index !== undefined) {
      obj.zIndex = index;
    }
    this.canvas.moveTo(obj.shape, obj.zIndex);
  }

  cloneCanvas(canvas) {
    canvas = canvas || this.canvas;
    const clone = document.createElement('canvas');
    clone.width = canvas.width;
    clone.height = canvas.height;
    canvas.wrapperEl.appendChild(clone);
    return clone;
  }

  setZoom(zoom) {
    const { width, height } = this.canvas;
    this.zoom = clamp(zoom, this.minZoom, this.maxZoom);
    this.dx = 0;
    this.dy = 0;
    this.x = width / 2.0;
    this.y = height / 2.0;
    this.update();
    process.nextTick(() => {
      this.update();
    });
  }

  getBoundingBox() {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    this.canvas.forEachObject(obj => {
      const coords = obj.getBounds();

      coords.forEach(point => {
        minX = Math.min(minX, point.x);
        maxX = Math.max(maxX, point.x);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      });
    });

    return {
      left: minX,
      right: maxX,
      top: minY,
      bottom: maxY
    };
  }

  getObjectTransformPositionById(objectId) {
    const group = this.canvas.getObjects().find(item => item.id === objectId);
    return group && group.getTransformPosition();
  }

  transformPoint(point) {
    return fabric.util.transformPoint(point, this.canvas.viewportTransform);
  }

  getPointerLength(num) {
    return Math.abs(this.getPointer({ x: 0, y: 0 }).x - this.getPointer({ x: num, y: 0 }).x);
  }

  getPointer(point, ignoreZoom) {
    const element = this.container;
    const scroll = fabric.util.getElementOffset(element);
    let pointer = {
      x: point.x + scroll.left,
      y: point.y + scroll.top
    };

    const upperCanvasEl = this.canvas.upperCanvasEl;
    const bounds = upperCanvasEl.getBoundingClientRect();
    let boundsWidth = bounds.width || 0;
    let boundsHeight = bounds.height || 0;
    let cssScale;

    if (!boundsWidth || !boundsHeight) {
      if ('top' in bounds && 'bottom' in bounds) {
        boundsHeight = Math.abs(bounds.top - bounds.bottom);
      }
      if ('right' in bounds && 'left' in bounds) {
        boundsWidth = Math.abs(bounds.right - bounds.left);
      }
    }

    this.canvas.calcOffset();
    pointer.x -= this.canvas._offset.left;
    pointer.y -= this.canvas._offset.top;
    if (!ignoreZoom) {
      pointer = this.canvas.restorePointerVpt(pointer);
    }

    const retinaScaling = this.canvas.getRetinaScaling();
    if (retinaScaling !== 1) {
      pointer.x /= retinaScaling;
      pointer.y /= retinaScaling;
    }

    if (boundsWidth === 0 || boundsHeight === 0) {
      // If bounds are not available (i.e. not visible), do not apply scale.
      cssScale = { width: 1, height: 1 };
    } else {
      cssScale = {
        width: upperCanvasEl.width / boundsWidth,
        height: upperCanvasEl.height / boundsHeight
      };
    }

    return {
      x: pointer.x * cssScale.width,
      y: pointer.y * cssScale.height
    };
  }

  setCursor(cursor) {
    this.container.style.cursor = cursor;
  }

  reset() {
    const { width, height } = this.canvas;
    this.zoom = this._options.zoom || 1;
    this.center = new Point();
    this.originX = -width / 2;
    this.originY = -height / 2;
    this.canvas.absolutePan({
      x: this.originX,
      y: this.originY
    });
    this.x = width / 2.0;
    this.y = height / 2.0;
    this.update();
    process.nextTick(() => {
      this.update();
    });
  }

  onResize(width, height) {
    const oldWidth = this.canvas.width;
    const oldHeight = this.canvas.height;

    // const point = this.getPointer(this.center);

    width = width || this.container.clientWidth;
    height = height || this.container.clientHeight;

    this.canvas.setWidth(width);
    this.canvas.setHeight(height);

    if (this.grid) {
      this.grid.setSize(width, height);
    }

    const dx = width / 2.0 - oldWidth / 2.0;
    const dy = height / 2.0 - oldHeight / 2.0;

    this.canvas.relativePan({
      x: dx,
      y: dy
    });

    this.update();

    // this.setView(this.center);
  }

  fitBounds(padding = 100) {
    // this.onResize();

    // if (this.bounds) {
    //   this.zoom = 1 / this.maxZoom;
    //   return;
    // }

    const { width, height } = this.canvas;

    this.originX = -width / 2;
    this.originY = -height / 2;

    const boundingBox = this.getBoundingBox();
    const { left, right, top, bottom } = boundingBox;

    this.center.x = (left + right) / 2.0;
    this.center.y = -(top + bottom) / 2.0;

    const boundWidth = Math.abs(left - right) + padding;
    const boundHeight = Math.abs(top - bottom) + padding;
    const scaleX = width / boundWidth;
    const scaleY = height / boundHeight;

    this.zoom = Math.max(scaleX, scaleY);

    this.canvas.setZoom(this.zoom);

    this.canvas.absolutePan({
      x: this.originX + this.center.x * this.zoom,
      y: this.originY - this.center.y * this.zoom
    });

    this.update();
    process.nextTick(() => {
      this.update();
    });
  }

  calculateMaxZoom() {
    if (!this.bounds) return false;
    const { height, width } = this.canvas;

    let { left, right, top, bottom } = this.getBoundingBox();

    if (left === Infinity) return false;

    const [pt, pr, pb, pl] = this.boundsPadding;
    left -= this.getPointerLength(pl);
    right += this.getPointerLength(pr);
    top -= this.getPointerLength(pt);
    bottom += this.getPointerLength(pb);

    const boundingBoxWidth = Math.abs(left - right) - 10;
    const boundingBoxHeight = Math.abs(top - bottom) - 10;

    const scaleX = width / boundingBoxWidth;
    const scaleY = height / boundingBoxHeight;
    const scale = Math.max(scaleX, scaleY);

    if (1 / scale !== this.maxZoom) {
      this.maxZoom = 1 / scale;

      if (1 / this.zoom > this.maxZoom) {
        this.zoom = 1 / this.maxZoom;
        this.canvas.setZoom(this.zoom);
        this.canvas.absolutePan({
          x: this.originX + this.center.x * this.zoom,
          y: this.originY - this.center.y * this.zoom
        });
      }
    }

    const topLeft = this.getPointer({ x: 0, y: 0 });
    const bottomRight = this.getPointer({ x: width, y: height });

    let limited = false;

    let diff = left - topLeft.x;

    if (diff > 0) {
      this.center.x += diff;
      this.dx = 0;
      limited = true;
    }

    diff = right - bottomRight.x;
    if (diff < 0) {
      this.center.x += diff;
      this.dx = 0;
      limited = true;
    }
    diff = top - topLeft.y;
    if (diff > 0) {
      this.center.y -= diff;
      this.dy = 0;
      limited = true;
    }

    diff = bottom - bottomRight.y;

    if (diff < 0) {
      this.center.y -= diff;
      this.dy = 0;
      limited = true;
    }

    if (limited) {
      this.canvas.absolutePan({
        x: this.originX + this.center.x * this.zoom,
        y: this.originY - this.center.y * this.zoom
      });
    }

    // this.canvas.setZoom(this.zoom);
    // this.canvas.absolutePan({
    //   x: this.originX + this.center.x * this.zoom,
    //   y: this.originY - this.center.y * this.zoom
    // });

    // console.log('x = ', this.x);
    // console.log('dx = ', this.dx);
    // console.log('center.x = ', this.center.x);
    // console.log('originX = ', this.originX);
    // console.log('boundingBox.left = ', left);
    // console.log('boundingBox.top = ', top);
    // console.log('topLeft.x = ', topLeft.x);
    // console.log('topLeft.y = ', topLeft.y);
    // console.log('width = ', bottomRight.x - topLeft.x);
    // console.log('viewportTransform = ', this.canvas.viewportTransform);

    this.limited = limited;
    return limited;
  }

  update() {
    const canvas = this.canvas;

    this.calculateMaxZoom();

    if (this.grid) {
      this.grid.update2({
        x: this.center.x,
        y: this.center.y,
        zoom: this.zoom
      });
      this.grid.render();
    }

    this.emit('update', this);
    canvas.zoomToPoint(new Point(this.x, this.y), this.zoom);

    if (this.isGrabMode() || this.isRight) {
      canvas.relativePan(new Point(this.dx, this.dy));
      this.emit('panning');
      this.setCursor('grab');
    } else {
      this.setCursor('pointer');
    }

    const now = Date.now();
    if (!this.lastUpdatedTime && Math.abs(this.lastUpdatedTime - now) < 100) {
      return;
    }
    this.lastUpdatedTime = now;

    const objects = canvas.getObjects();
    let hasKeepZoom = false;
    for (let i = 0; i < objects.length; i += 1) {
      const object = objects[i];
      if (object.keepOnZoom) {
        object.set('scaleX', 1.0 / this.zoom);
        object.set('scaleY', 1.0 / this.zoom);
        object.setCoords();
        hasKeepZoom = true;
        this.emit(`${object.class}scaling`, object);
      }
    }
    if (hasKeepZoom) canvas.requestRenderAll();
  }

  panzoom(e) {
    // if (this.limited) return;
    // enable interactions
    const { width, height } = this.canvas;
    // shift start
    const zoom = clamp(-e.dz, -height * 0.75, height * 0.75) / height;

    const prevZoom = 1 / this.zoom;
    let curZoom = prevZoom * (1 - zoom);
    curZoom = clamp(curZoom, this.minZoom, this.maxZoom);

    let { x, y } = this.center;

    // pan
    const oX = 0.5;
    const oY = 0.5;
    if (this.isGrabMode() || e.isRight) {
      x -= prevZoom * e.dx;
      y += prevZoom * e.dy;
      this.setCursor('grab');
    } else {
      this.setCursor('pointer');
    }

    if (this.zoomEnabled) {
      const tx = e.x / width - oX;
      x -= width * (curZoom - prevZoom) * tx;
      const ty = oY - e.y / height;
      y -= height * (curZoom - prevZoom) * ty;
    }

    this.center.setX(x);
    this.center.setY(y);
    this.zoom = 1 / curZoom;
    this.dx = e.dx;
    this.dy = e.dy;
    this.x = e.x0;
    this.y = e.y0;
    this.isRight = e.isRight;
    this.update();
  }

  setView(view) {
    this.dx = 0;
    this.dy = 0;
    this.x = 0;
    this.y = 0;
    view.y *= -1;

    const dx = this.center.x - view.x;
    const dy = -this.center.y + view.y;

    this.center.copy(view);

    this.canvas.relativePan(new Point(dx * this.zoom, dy * this.zoom));

    this.canvas.renderAll();

    this.update();

    process.nextTick(() => {
      this.update();
    });
  }

  registerListeners() {
    const vm = this;

    this.canvas.on('object:scaling', e => {
      if (e.target.class) {
        vm.emit(`${e.target.class}:scaling`, e.target.parent);
        e.target.parent.emit('scaling', e.target.parent);
        return;
      }
      const group = e.target;
      if (!group.getObjects) return;

      const objects = group.getObjects();
      group.removeWithUpdate();
      for (let i = 0; i < objects.length; i += 1) {
        const object = objects[i];
        object.orgYaw = object.parent.yaw || 0;
        object.fire('moving', object.parent);
        vm.emit(`${object.class}:moving`, object.parent);
      }
      vm.update();
      vm.canvas.requestRenderAll();
    });

    this.canvas.on('object:rotating', e => {
      if (e.target.class) {
        vm.emit(`${e.target.class}:rotating`, e.target.parent, e.target.angle);
        e.target.parent.emit('rotating', e.target.parent, e.target.angle);
        return;
      }
      const group = e.target;
      if (!group.getObjects) return;
      const objects = group.getObjects();
      for (let i = 0; i < objects.length; i += 1) {
        const object = objects[i];
        if (object.class === 'marker') {
          object._set('angle', -group.angle);
          object.parent.yaw = -group.angle + (object.orgYaw || 0);
          // object.orgYaw = object.parent.yaw;
          object.fire('moving', object.parent);
          vm.emit(`${object.class}:moving`, object.parent);
          object.fire('rotating', object.parent);
          vm.emit(`${object.class}:rotating`, object.parent);
        }
      }
      this.update();
    });

    this.canvas.on('object:moving', e => {
      if (e.target.class) {
        vm.emit(`${e.target.class}:moving`, e.target.parent);
        e.target.parent.emit('moving', e.target.parent);
        return;
      }
      const group = e.target;
      if (!group.getObjects) return;
      const objects = group.getObjects();
      for (let i = 0; i < objects.length; i += 1) {
        const object = objects[i];
        if (object.class) {
          object.fire('moving', object.parent);
          vm.emit(`${object.class}:moving`, object.parent);
        }
      }
      this.update();
    });

    this.canvas.on('object:moved', e => {
      if (e.target.class) {
        vm.emit(`${e.target.class}dragend`, e);
        vm.emit(`${e.target.class}:moved`, e.target.parent);
        e.target.parent.emit('moved', e.target.parent);
        this.update();
        return;
      }
      const group = e.target;
      if (!group.getObjects) return;
      const objects = group.getObjects();
      for (let i = 0; i < objects.length; i += 1) {
        const object = objects[i];
        if (object.class) {
          object.fire('moved', object.parent);
          vm.emit(`${object.class}:moved`, object.parent);
        }
      }
      this.update();
    });

    this.canvas.on('selection:cleared', e => {
      const objects = e.deselected;
      if (!objects || !objects.length) return;
      for (let i = 0; i < objects.length; i += 1) {
        const object = objects[i];
        if (object.class === 'marker') {
          object._set('angle', 0);
          object._set('scaleX', 1 / vm.zoom);
          object._set('scaleY', 1 / vm.zoom);
          if (object.parent) {
            object.parent.inGroup = false;
          }
          object.fire('moving', object.parent);
        }
      }
    });
    this.canvas.on('selection:created', e => {
      const objects = e.selected;
      if (!objects || objects.length < 2) return;
      for (let i = 0; i < objects.length; i += 1) {
        const object = objects[i];
        if (object.class && object.parent) {
          object.parent.inGroup = true;
          object.orgYaw = object.parent.yaw || 0;
        }
      }
    });
    this.canvas.on('selection:updated', e => {
      const objects = e.selected;
      if (!objects || objects.length < 2) return;
      for (let i = 0; i < objects.length; i += 1) {
        const object = objects[i];
        if (object.class && object.parent) {
          object.parent.inGroup = true;
          object.orgYaw = object.parent.yaw || 0;
        }
      }
    });

    this.canvas.on('mouse:down', e => {
      vm.dragObject = e.target;
      vm.mouseMove = false;
    });

    this.canvas.on('mouse:move', e => {
      if (this.isMeasureMode()) {
        this.measurement.onMouseMove(e);
      }
      if (vm.dragObject && vm.dragObject.clickable) {
        if (vm.dragObject === e.target) {
          vm.dragObject.dragging = true;
        } else {
          vm.dragObject.dragging = false;
        }
      }
      this.isRight = false;
      if ('which' in e.e) {
        // Gecko (Firefox), WebKit (Safari/Chrome) & Opera
        this.isRight = e.e.which === 3;
      } else if ('button' in e.e) {
        // IE, Opera
        this.isRight = e.e.button === 2;
      }

      vm.mouseMove = true;
      vm.emit('mouse:move', e);
    });

    this.canvas.on('mouse:up', e => {
      if (this.isMeasureMode()) {
        this.measurement.onClick(e);
      }

      this.isRight = false;
      this.dx = 0;
      this.dy = 0;

      if (!vm.dragObject || !e.target || !e.target.selectable) {
        e.target = null;
        if (!vm.mouseMove) {
          vm.emit('mouse:click', e);
        }
      }
      if (vm.dragObject && vm.dragObject.clickable) {
        if (vm.dragObject !== e.target) return;
        if (!vm.dragObject.dragging && !vm.modeToggleByKey) {
          vm.emit(`${vm.dragObject.class}:click`, vm.dragObject.parent);
        }
        vm.dragObject.dragging = false;
      }
      vm.dragObject = null;
    });

    window.addEventListener('resize', () => {
      vm.onResize();
    });

    // document.addEventListener('keyup', () => {
    //   if (this.modeToggleByKey && this.isGrabMode()) {
    //     this.setModeAsSelect();
    //     this.modeToggleByKey = false;
    //   }
    // });

    // document.addEventListener('keydown', event => {
    //   if (event.ctrlKey || event.metaKey) {
    //     if (this.isSelectMode()) {
    //       this.setModeAsGrab();
    //     }
    //     this.modeToggleByKey = true;
    //   }
    // });
  }

  unregisterListeners() {
    this.canvas.off('object:moving');
    this.canvas.off('object:moved');
  }

  getMarkerById(id) {
    const objects = this.canvas.getObjects();
    for (let i = 0; i < objects.length; i += 1) {
      const obj = objects[i];
      if (obj.class === 'marker' && obj.id === id) {
        return obj.parent;
      }
    }
    return null;
  }

  getMarkers() {
    const list = [];
    const objects = this.canvas.getObjects();
    for (let i = 0; i < objects.length; i += 1) {
      const obj = objects[i];
      if (obj.class === 'marker') {
        list.push(obj.parent);
      }
    }
    return list;
  }
}

export const map = (container, options) => new Map(container, options);
